/**
 * Create WRO Dialog
 *
 * Creates a real ShipBob warehouse receiving order (WRO) from a PO's line
 * items. The WRO carries the PO number (plus any note the person adds, e.g.
 * "275 US Wedge DHAP"), so the existing PO↔WRO auto-match links it back with
 * no extra bookkeeping. Nothing is sent until Create — the dialog shows exactly
 * what ShipBob will receive. Once created, the shipping mark can be downloaded.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import ShippingMarkButton from "shared/components/ShippingMarkButton";
import InventoryService from "services/InventoryService";
import { POLineItem } from "models/InventoryModel";

const PACKAGE_TYPES = [
  { value: "FloorLoadedContainer", label: "Floor-loaded container" },
  { value: "Pallet", label: "Pallets" },
  { value: "Package", label: "Parcel / boxes" },
] as const;
type PackageType = (typeof PACKAGE_TYPES)[number]["value"];

// PO countryOfDestination values → ShipBob FC country codes
const FC_COUNTRY: Record<string, string> = { US: "US", UK: "GB", AU: "AU", CA: "CA" };

type FulfillmentCenter = { id: number; name: string; city: string | null; state: string | null; country: string | null; replacedById?: number | null };

interface CreateWroDialogProps {
  open: boolean;
  onClose: () => void;
  poNumber: string;
  arriveByDate: string;
  countryOfDestination?: string | null;
  containerType?: string | null;
  lineItems: POLineItem[];
  /** The PO's chosen ship-to FC — wins over history when set */
  defaultFulfillmentCenterId?: number | null;
  /** FC names of past WROs, most recent first — the best guess for where this one goes */
  recentFulfillmentCenterNames?: string[];
  onCreated: (wro: { wroId: string; fulfillmentCenterName: string | null }) => void;
}

// Mirrors the server's wroPoNumberMatchesPo (the po_wro_links auto-match rule): the PO number,
// optionally followed by a non-alphanumeric character and a note.
const wroPoNumberLinksBack = (wroPoNumber: string, poNumber: string): boolean => {
  const theirs = wroPoNumber.trim().toUpperCase();
  const ours = poNumber.trim().toUpperCase();
  return ours.length > 0 && theirs.startsWith(ours) && !/[A-Z0-9]/.test(theirs.charAt(ours.length));
};

export default function CreateWroDialog({
  open,
  onClose,
  poNumber,
  arriveByDate,
  countryOfDestination,
  containerType,
  lineItems,
  defaultFulfillmentCenterId = null,
  recentFulfillmentCenterNames = [],
  onCreated,
}: CreateWroDialogProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const [fulfillmentCenters, setFulfillmentCenters] = useState<FulfillmentCenter[] | null>(null);
  const [fulfillmentCenterId, setFulfillmentCenterId] = useState<number | "">("");
  // LCL freight arrives palletized; full containers arrive floor-loaded
  const isLcl = (containerType || "").toUpperCase() === "LCL";
  const [packageType, setPackageType] = useState<PackageType>(isLcl ? "Pallet" : "FloorLoadedContainer");
  const [expectedArrivalDate, setExpectedArrivalDate] = useState(arriveByDate);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [wroPoNumber, setWroPoNumber] = useState(poNumber);
  const [createdWro, setCreatedWro] = useState<{ wroId: string; purchaseOrderNumber: string } | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Seed the form each time the dialog opens for a PO
  useEffect(() => {
    if (!open) return;
    setExpectedArrivalDate(arriveByDate);
    setPackageType(isLcl ? "Pallet" : "FloorLoadedContainer");
    setQuantities(Object.fromEntries(lineItems.map(li => [li.inventoryId, String(li.quantity)])));
    setTrackingNumber("");
    setWroPoNumber(poNumber);
    setCreatedWro(null);
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || fulfillmentCenters) return;
    service.getReceivingFulfillmentCenters().then(response => {
      if (response) setFulfillmentCenters(response.fulfillmentCenters);
      else setErrorMessage("Failed to load ShipBob fulfillment centers");
    });
  }, [open, fulfillmentCenters, service]);

  // FCs for the PO's destination country first (others still selectable below)
  const fcCountry = FC_COUNTRY[countryOfDestination || "US"] || "US";
  const sortedFcs = useMemo(() => {
    // Centers we no longer ship to are never offered for a new WRO
    const list = (fulfillmentCenters || []).filter(fc => fc.replacedById == null);
    return [...list].sort((a, b) =>
      Number(b.country === fcCountry) - Number(a.country === fcCountry) || a.name.localeCompare(b.name));
  }, [fulfillmentCenters, fcCountry]);

  // Default FC: where the most recent in-country WRO went, else the country's
  // first receiving-enabled FC. Only a default — always changeable.
  useEffect(() => {
    if (!open || !fulfillmentCenters || fulfillmentCenterId !== "") return;
    const inCountry = fulfillmentCenters.filter(fc => fc.country === fcCountry && fc.replacedById == null);
    const recentName = recentFulfillmentCenterNames.find(name => inCountry.some(fc => fc.name === name));
    // The PO's ship-to wins — unless it is a center we no longer use, then its replacement does
    const poShipTo = fulfillmentCenters.find(fc => fc.id === defaultFulfillmentCenterId);
    const chosen = (poShipTo?.replacedById != null ? fulfillmentCenters.find(fc => fc.id === poShipTo.replacedById) : poShipTo)
      || inCountry.find(fc => fc.name === recentName) || inCountry[0];
    if (chosen) setFulfillmentCenterId(chosen.id);
  }, [open, fulfillmentCenters, fulfillmentCenterId, defaultFulfillmentCenterId, recentFulfillmentCenterNames, fcCountry]);

  const items = lineItems
    .map(li => ({ inventoryId: Number(li.inventoryId), name: li.inventoryName, quantity: parseInt(quantities[li.inventoryId] ?? "", 10) }))
    .filter(item => Number.isFinite(item.quantity) && item.quantity >= 1);
  const totalUnits = items.reduce((sum, item) => sum + item.quantity, 0);
  const excludedCount = lineItems.length - items.length;

  // ShipBob rejects any other character in a WRO's PO number
  const wroPoNumberHasBadCharacters = !/^[A-Za-z0-9 _-]*$/.test(wroPoNumber.trim());
  const wroPoNumberIsValid = wroPoNumberLinksBack(wroPoNumber, poNumber) && !wroPoNumberHasBadCharacters;
  const placeholderTracking = `PO${poNumber.replace(/[^A-Za-z0-9]/g, "").toUpperCase()}`;
  const isFormValid = fulfillmentCenterId !== "" && wroPoNumberIsValid
    && /^\d{4}-\d{2}-\d{2}$/.test(expectedArrivalDate) && items.length > 0;

  const handleCreate = async () => {
    if (!isFormValid || typeof fulfillmentCenterId !== "number") return;
    setIsCreating(true);
    setErrorMessage(null);
    try {
      const { wro } = await service.createWro({
        poNumber,
        wroPoNumber: wroPoNumber.trim(),
        fulfillmentCenterId,
        packageType,
        expectedArrivalDate,
        trackingNumber: trackingNumber.trim(),
        items: items.map(({ inventoryId, quantity }) => ({ inventoryId, quantity })),
      });
      onCreated({ wroId: wro.wroId, fulfillmentCenterName: wro.fulfillmentCenterName });
      // Stay open on a confirmation so the shipping mark can be downloaded straight away
      setCreatedWro({ wroId: wro.wroId, purchaseOrderNumber: wro.purchaseOrderNumber || wroPoNumber.trim() });
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const selectedFc = sortedFcs.find(fc => fc.id === fulfillmentCenterId);
  const replacedPoShipTo = (fulfillmentCenters || []).find(fc => fc.id === defaultFulfillmentCenterId && fc.replacedById != null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create ShipBob receiving order — {poNumber}</DialogTitle>
      {createdWro ? (
        <>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              WRO {createdWro.wroId} created in ShipBob as "{createdWro.purchaseOrderNumber}" and linked to {poNumber}.
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Download the shipping mark to send to the factory from this WRO in ShipBob. The same button is on the WRO's row on the PO.
            </Typography>
            <ShippingMarkButton wroId={createdWro.wroId} variant="outlined" />
            {!trackingNumber.trim() && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No container number was entered, so ShipBob has the placeholder "{placeholderTracking}". Add the real
                container number on the WRO in ShipBob once you have it.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={onClose}>Done</Button>
          </DialogActions>
        </>
      ) : (
      <>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tells ShipBob what's arriving so they can receive it. The WRO carries this
          PO's number and links back to it automatically.
        </Typography>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}

        {replacedPoShipTo && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This PO's ship-to is <strong>{replacedPoShipTo.name}</strong>, which is no longer used, so the WRO is set
            to go to {selectedFc?.name ?? "its replacement"}. Change the PO's ship-to as well — it prints on the PO document.
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <TextField
            size="small"
            label="PO # on the WRO"
            value={wroPoNumber}
            onChange={(e) => setWroPoNumber(e.target.value)}
            error={!wroPoNumberIsValid}
            helperText={wroPoNumberIsValid
              ? `Add a note after the PO number if it helps, e.g. "${poNumber} DHAP Wedge"`
              : wroPoNumberHasBadCharacters
                ? "ShipBob only accepts letters, numbers, spaces, hyphens and underscores"
                : `Must start with "${poNumber}" followed by a space — otherwise the WRO will not link back to this PO`}
            sx={{ width: 320 }}
          />
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Fulfillment center</InputLabel>
            <Select
              value={fulfillmentCenterId}
              label="Fulfillment center"
              onChange={(e) => setFulfillmentCenterId(Number(e.target.value))}
            >
              {sortedFcs.map(fc => (
                <MenuItem key={fc.id} value={fc.id}>
                  {fc.name}{fc.city ? ` — ${fc.city}${fc.state ? `, ${fc.state}` : ""}` : ""}{fc.country !== fcCountry ? ` (${fc.country})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Arriving as</InputLabel>
            <Select value={packageType} label="Arriving as" onChange={(e) => setPackageType(e.target.value as PackageType)}>
              {PACKAGE_TYPES.map(pt => (
                <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            label="Expected arrival"
            value={moment(expectedArrivalDate)}
            onChange={(newValue: any) => {
              if (newValue && newValue.isValid()) setExpectedArrivalDate(newValue.format("YYYY-MM-DD"));
            }}
            slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
          />
          <TextField
            size="small"
            label="Container # / tracking # (optional)"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            helperText={`Leave blank if you don't have it yet — ShipBob gets "${placeholderTracking}" and you add the container # there later`}
            sx={{ width: 320 }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Contents (from the PO — adjust if this shipment differs)</Typography>
        {fulfillmentCenters === null ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell width={140}>Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map(li => (
                <TableRow key={li.inventoryId}>
                  <TableCell>{li.inventoryName || `Inventory ${li.inventoryId}`}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={quantities[li.inventoryId] ?? ""}
                      onChange={(e) => setQuantities(prev => ({ ...prev, [li.inventoryId]: e.target.value }))}
                      inputProps={{ min: 0, step: 1 }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {excludedCount > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {excludedCount} line{excludedCount === 1 ? "" : "s"} at quantity 0 will be left off the WRO
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {isFormValid && selectedFc
            ? `${totalUnits.toLocaleString()} units in ${items.length} SKU${items.length === 1 ? "" : "s"} → ${selectedFc.name}`
            : !wroPoNumberIsValid ? "Fix the PO # on the WRO" : "Choose a fulfillment center, arrival date and at least one item"}
        </Typography>
        <Box>
          <Button onClick={onClose} disabled={isCreating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!isFormValid || isCreating}>
            {isCreating ? "Creating in ShipBob…" : "Create WRO"}
          </Button>
        </Box>
      </DialogActions>
      </>
      )}
    </Dialog>
  );
}
